import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart3, Plus, Pencil, Trash2, X, Check, Search, Building2, User, Calendar, DollarSign, FileText, ClipboardCheck, UserCheck, Send, FileInput as FileInvoice, Receipt, Instagram, Youtube, MessageCircle, TwitchIcon, PlusCircle, ArrowRight } from 'lucide-react';
import { Collab, Influencer, Client, collabsRef, influencersRef, clientsRef, createDocument, getDocuments, updateDocument, deleteDocument } from '../lib/firebase';
import { Dialog, Switch } from '@headlessui/react';

const PLATFORM_ICONS = {
  Instagram: {
    icon: 'https://cdn.simpleicons.org/instagram',
    color: 'text-gray-400 opacity-75 hover:opacity-100',
  },
  YouTube: {
    icon: 'https://cdn.simpleicons.org/youtube',
    color: 'text-gray-400 opacity-75 hover:opacity-100',
  },
  TikTok: {
    icon: 'https://cdn.simpleicons.org/tiktok',
    color: 'text-gray-400 opacity-75 hover:opacity-100',
  },
  Twitch: {
    icon: 'https://cdn.simpleicons.org/twitch',
    color: 'text-gray-400 opacity-75 hover:opacity-100',
  },
  Snapchat: {
    icon: 'https://cdn.simpleicons.org/snapchat',
    color: 'text-gray-400 opacity-75 hover:opacity-100',
  },
  X: {
    icon: 'https://cdn.simpleicons.org/x',
    color: 'text-gray-400 opacity-75 hover:opacity-100',
  },
  Facebook: {
    icon: 'https://cdn.simpleicons.org/facebook',
    color: 'text-gray-400 opacity-75 hover:opacity-100',
  },
  Pinterest: {
    icon: 'https://cdn.simpleicons.org/pinterest',
    color: 'text-gray-400 opacity-75 hover:opacity-100',
    posted: false
  }
} as const;

const CONTENT_TYPES = [
  { value: 'post_instagram', label: 'Post Instagram' },
  { value: 'story_instagram', label: 'Story Instagram' },
  { value: 'reel_instagram', label: 'Réel Instagram' },
  { value: 'tiktok', label: 'TikTok' },
  { value: 'story_snapchat', label: 'Story Snapchat' },
  { value: 'twitch', label: 'Live Twitch' },
  { value: 'youtube', label: 'Intégration YouTube' },
  { value: 'ugc', label: 'UGC (User Generated Content)' },
] as const;

const COLLAB_TYPES = [
  { value: 'collaboration_entrante', label: 'Collaboration entrante' },
  { value: 'collaboration_sortante', label: 'Collaboration sortante' },
  { value: 'casting_interne', label: 'Casting interne' },
  { value: 'casting_externe', label: 'Casting externe' },
] as const;

function Collaborations() {
  const navigate = useNavigate();
  const [collabs, setCollabs] = useState<Collab[]>([]);
  const [influencers, setInfluencers] = useState<Influencer[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedCollab, setSelectedCollab] = useState<Collab | null>(null);
  const [isClientSelectorOpen, setIsClientSelectorOpen] = useState(false);
  const [clientSearch, setClientSearch] = useState('');
  const [isInfluencerSelectorOpen, setIsInfluencerSelectorOpen] = useState(false);
  const [influencerSearch, setInfluencerSearch] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [formData, setFormData] = useState<Partial<Collab>>({
    name: '',
    type: 'collaboration_entrante',
    date: new Date(),
    contactDate: new Date(),
    signatureDate: new Date(),
    brand: '',
    influencers: [],
    quote_sent: false,
    quote_number: '',
    contract_signed: false,
    casting_validated: false,
    manager: '',
    post_progress: '0',
    billing_status: '',
    invoice_number: '',
    payment_received: false,
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setIsLoading(true);
      const [collabsData, influencersData, clientsData] = await Promise.all([
        getDocuments<Collab>(collabsRef),
        getDocuments<Influencer>(influencersRef),
        getDocuments<Client>(clientsRef),
      ]);
      setCollabs(collabsData);
      setInfluencers(influencersData.filter(inf => !inf.archived));
      setClients(clientsData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const sanitizedFormData = {
        ...formData,
        date: formData.date || new Date(),
        contactDate: formData.contactDate || new Date(),
        signatureDate: formData.signatureDate || new Date(),
        quote_number: formData.quote_number ?? '',
      };

      if (selectedCollab?.id) {
        await updateDocument(collabsRef, selectedCollab.id, sanitizedFormData);
      } else {
        await createDocument(collabsRef, {
          ...sanitizedFormData,
          createdAt: new Date(),
          updatedAt: new Date(),
        } as Collab);
      }
      await loadData();
      handleCloseModal();
    } catch (error) {
      console.error('Error saving collaboration:', error);
    }
  };

  const handleDelete = async () => {
    try {
      if (!selectedCollab?.id) return;
      await deleteDocument(collabsRef, selectedCollab.id);
      await loadData();
      setIsDeleteModalOpen(false);
      setSelectedCollab(null);
    } catch (error) {
      console.error('Error deleting collaboration:', error);
    }
  };

  const handleEdit = (collab: Collab) => {
    setSelectedCollab(collab);
    setFormData({
      name: collab.name,
      type: collab.type,
      date: collab.date ? new Date(collab.date.seconds * 1000) : new Date(),
      contactDate: collab.contactDate ? new Date(collab.contactDate.seconds * 1000) : new Date(),
      signatureDate: collab.signatureDate ? new Date(collab.signatureDate.seconds * 1000) : new Date(),
      brand: collab.brand,
      influencers: [...collab.influencers],
      quote_sent: collab.quote_sent,
      quote_number: collab.quote_number || '',
      contract_signed: collab.contract_signed,
      casting_validated: collab.casting_validated,
      manager: collab.manager,
      post_progress: collab.post_progress,
      billing_status: collab.billing_status,
      invoice_number: collab.invoice_number,
      payment_received: collab.payment_received,
    });
    setIsModalOpen(true);
  };

  const handleSelectClient = (client: Client) => {
    setFormData(prev => ({
      ...prev,
      brand: client.brand_name
    }));
    setIsClientSelectorOpen(false);
  };

  const handleSelectInfluencer = (influencer: Influencer) => {
    setFormData(prev => ({
      ...prev,
      influencers: [
        ...(prev.influencers || []),
        {
          fullName: influencer.fullName,
          deliverables: [],
          rate: '0',
          creator_invoice_sent: false
        }
      ]
    }));
    setIsInfluencerSelectorOpen(false);
  };

  const handleAddDeliverable = (influencerIndex: number) => {
    setFormData(prev => {
      const updatedInfluencers = [...(prev.influencers || [])];
      updatedInfluencers[influencerIndex] = {
        ...updatedInfluencers[influencerIndex],
        deliverables: [
          ...(updatedInfluencers[influencerIndex].deliverables || []),
          {
            type: CONTENT_TYPES[0].value,
            description: '',
            price: '0'
          }
        ]
      };

      const totalRate = updatedInfluencers[influencerIndex].deliverables.reduce(
        (sum, deliverable) => sum + parseFloat(deliverable.price || '0'),
        0
      ).toString();

      updatedInfluencers[influencerIndex].rate = totalRate;

      return {
        ...prev,
        influencers: updatedInfluencers
      };
    });
  };

  const handleUpdateDeliverable = (
    influencerIndex: number,
    deliverableIndex: number,
    field: 'type' | 'description' | 'price',
    value: string
  ) => {
    setFormData(prev => {
      const updatedInfluencers = [...(prev.influencers || [])];
      const updatedDeliverables = [...(updatedInfluencers[influencerIndex].deliverables || [])];
      
      updatedDeliverables[deliverableIndex] = {
        ...updatedDeliverables[deliverableIndex],
        [field]: value
      };

      const totalRate = updatedDeliverables.reduce(
        (sum, deliverable) => sum + parseFloat(deliverable.price || '0'),
        0
      ).toString();

      updatedInfluencers[influencerIndex] = {
        ...updatedInfluencers[influencerIndex],
        deliverables: updatedDeliverables,
        rate: totalRate
      };

      return {
        ...prev,
        influencers: updatedInfluencers
      };
    });
  };

  const handleRemoveDeliverable = (influencerIndex: number, deliverableIndex: number) => {
    setFormData(prev => {
      const updatedInfluencers = [...(prev.influencers || [])];
      const updatedDeliverables = updatedInfluencers[influencerIndex].deliverables.filter(
        (_, index) => index !== deliverableIndex
      );

      const totalRate = updatedDeliverables.reduce(
        (sum, deliverable) => sum + parseFloat(deliverable.price || '0'),
        0
      ).toString();

      updatedInfluencers[influencerIndex] = {
        ...updatedInfluencers[influencerIndex],
        deliverables: updatedDeliverables,
        rate: totalRate
      };

      return {
        ...prev,
        influencers: updatedInfluencers
      };
    });
  };

  const handleRemoveInfluencer = (index: number) => {
    setFormData(prev => ({
      ...prev,
      influencers: prev.influencers?.filter((_, i) => i !== index)
    }));
  };

  const resetForm = () => {
    setFormData({
      name: '',
      type: 'collaboration_entrante',
      date: new Date(),
      contactDate: new Date(),
      signatureDate: new Date(),
      brand: '',
      influencers: [],
      quote_sent: false,
      quote_number: '',
      contract_signed: false,
      casting_validated: false,
      manager: '',
      post_progress: '0',
      billing_status: '',
      invoice_number: '',
      payment_received: false,
    });
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedCollab(null);
    resetForm();
  };

  const formatDate = (date: Date | { seconds: number } | undefined) => {
    if (!date) return 'Date inconnue';
    
    if ('seconds' in date) {
      return new Date(date.seconds * 1000).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    }
    
    return new Date(date).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount: string | number) => {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(num)) return '0 €';
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(num);
  };

  const getCollabTypeLabel = (type: string) => {
    return COLLAB_TYPES.find(t => t.value === type)?.label || type;
  };

  const getCampaignStatus = (campaign: Collab) => {
    if (campaign.payment_received) return { label: 'Terminée', className: 'bg-green-500/20 text-green-300 border-green-500/20' };
    if (campaign.quote_sent) return { label: 'En cours', className: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/20' };
    return { label: 'En négociation', className: 'bg-blue-500/20 text-blue-300 border-blue-500/20' };
  };

  const filteredCollabs = collabs.filter(collab => {
    const matchesSearch = 
      collab.name.toLowerCase().includes(search.toLowerCase()) ||
      collab.brand.toLowerCase().includes(search.toLowerCase());
    
    const status = collab.payment_received ? 'completed' : collab.quote_sent ? 'in_progress' : 'pending';
    const matchesStatus = statusFilter === 'all' || status === statusFilter;
    
    const matchesType = typeFilter === 'all' || collab.type === typeFilter;

    return matchesSearch && matchesType && matchesStatus;
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-accent-purple to-accent-blue flex items-center justify-center">
            <BarChart3 className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-white">Collaborations</h1>
            <p className="text-gray-400">Gérez vos campagnes d'influence</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setIsModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2 bg-accent-blue text-white rounded-lg hover:bg-accent-blue/90 transition-colors"
          >
            <Plus className="w-5 h-5" />
            Nouvelle collaboration
          </button>
        </div>
      </div>

      <div className="bg-dark-50 rounded-xl border border-dark-200 p-6">
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher une collaboration..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10"
              />
            </div>
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full sm:w-48"
          >
            <option value="all">Tous les statuts</option>
            <option value="pending">En attente</option>
            <option value="in_progress">En cours</option>
            <option value="completed">Terminées</option>
          </select>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="w-full sm:w-48"
          >
            <option value="all">Tous les types</option>
            {COLLAB_TYPES.map((type) => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </select>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-blue"></div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-dark-200">
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Nom</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Type</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Marque</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Date de contact</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Date de signature</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Chef de projet</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Statut</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">État</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-gray-400">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-dark-200">
                {filteredCollabs.map((collab) => (
                  <tr 
                    key={collab.id} 
                    className="hover:bg-dark-100/50 cursor-pointer"
                    onClick={() => {
                      setSelectedCollab(collab);
                      setIsDetailModalOpen(true);
                    }}
                  >
                    <td className="py-3 px-4 text-white">{collab.name}</td>
                    <td className="py-3 px-4 text-gray-400">{getCollabTypeLabel(collab.type)}</td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-accent-blue" />
                        <span className="text-gray-400">{collab.brand}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-gray-400">{formatDate(collab.contactDate)}</td>
                    <td className="py-3 px-4 text-gray-400">{formatDate(collab.signatureDate)}</td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-accent-purple" />
                        <span className="text-gray-400">{collab.manager}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getCampaignStatus(collab).className}`}>
                        {getCampaignStatus(collab).label}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-1.5" title="Devis envoyé">
                          <Send className={`w-4 h-4 ${collab.quote_sent ? 'text-green-400' : 'text-gray-400'}`} />
                          <span className="text-xs text-gray-400">Devis</span>
                        </div>
                        <div className="flex items-center gap-1.5" title="Contrat signé">
                          <FileText className={`w-4 h-4 ${collab.contract_signed ? 'text-green-400' : 'text-gray-400'}`} />
                          <span className="text-xs text-gray-400">Contrat</span>
                        </div>
                        <div className="flex items-center gap-1.5" title="Casting validé">
                          <UserCheck className={`w-4 h-4 ${collab.casting_validated ? 'text-green-400' : 'text-gray-400'}`} />
                          <span className="text-xs text-gray-400">Casting</span>
                        </div>
                        <div className="flex items-center gap-1.5" title="Post publié">
                          <ClipboardCheck className={`w-4 h-4 ${collab.post_progress === '100' ? 'text-green-400' : 'text-gray-400'}`} />
                          <span className="text-xs text-gray-400">Post</span>
                        </div>
                        <div className="flex items-center gap-1.5" title="Facture envoyée">
                          <FileInvoice className={`w-4 h-4 ${collab.invoice_number ? 'text-green-400' : 'text-gray-400'}`} />
                          <span className="text-xs text-gray-400">Facture</span>
                        </div>
                        <div className="flex items-center gap-1.5" title="Paiement reçu">
                          <DollarSign className={`w-4 h-4 ${collab.payment_received ? 'text-green-400' : 'text-gray-400'}`} />
                          <span className="text-xs text-gray-400">Paiement</span>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEdit(collab);
                        }}
                        className="text-accent-blue hover:text-accent-blue/80 mx-2"
                      >
                        <Pencil className="w-5 h-5" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedCollab(collab);
                          setIsDeleteModalOpen(true);
                        }}
                        className="text-red-500 hover:text-red-400 mx-2"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Campaign Details Modal */}
      <Dialog
        open={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        className="fixed inset-0 z-10 overflow-y-auto"
      >
        <div className="flex items-center justify-center min-h-screen p-4">
          <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-30" />
          <div className="relative bg-dark-50 rounded-xl max-w-4xl w-full mx-4 p-6 border border-dark-200">
            {selectedCollab && (
              <>
                <div className="absolute top-4 right-4">
                  <button
                    onClick={() => setIsDetailModalOpen(false)}
                    className="text-gray-400 hover:text-gray-300"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-2xl font-bold text-white">{selectedCollab.name}</h2>
                      <p className="text-gray-400">{selectedCollab.brand}</p>
                    </div>
                    <button
                      onClick={() => {
                        setIsDetailModalOpen(false);
                        handleEdit(selectedCollab);
                      }}
                      className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-accent-blue rounded-lg hover:bg-accent-blue/90"
                    >
                      <Pencil className="w-4 h-4" />
                      Modifier
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h3 className="text-lg font-medium text-white mb-4">Informations</h3>
                      <div className="bg-dark-100 rounded-lg p-4 space-y-4">
                        <div className="flex items-center gap-2 text-gray-400">
                          <Building2 className="w-4 h-4" />
                          <span>Client : {selectedCollab.brand}</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-400">
                          <Building2 className="w-4 h-4" />
                          <span>Type: {COLLAB_TYPES.find(t => t.value === selectedCollab.type)?.label}</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-400">
                          <Calendar className="w-4 h-4" />
                          <span>Première prise de contact: {formatDate(selectedCollab.contactDate)}</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-400">
                          <Calendar className="w-4 h-4" />
                          <span>Date de signature: {formatDate(selectedCollab.signatureDate)}</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-400">
                          <User className="w-4 h-4" />
                          <span>Chef de projet: {selectedCollab.manager}</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-400">
                          <DollarSign className="w-4 h-4" />
                          <span>Montant total: {formatCurrency(
                            selectedCollab.influencers.reduce((sum, inf) => sum + parseFloat(inf.rate || '0'), 0)
                          )}</span>
                        </div>
                      </div>

                      <h3 className="text-lg font-medium text-white mb-4 mt-6">État de la campagne</h3>
                      <div className="bg-dark-100 rounded-lg p-4 space-y-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 text-gray-400">
                            <Send className="w-4 h-4" />
                            <span>Devis envoyé</span>
                          </div>
                          {selectedCollab.quote_sent ? (
                            <div className="text-green-400">✓</div>
                          ) : (
                            <div className="text-red-400">×</div>
                          )}
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 text-gray-400">
                            <FileText className="w-4 h-4" />
                            <span>Contrat signé</span>
                          </div>
                          {selectedCollab.contract_signed ? (
                            <div className="text-green-400">✓</div>
                          ) : (
                            <div className="text-red-400">×</div>
                          )}
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 text-gray-400">
                            <UserCheck className="w-4 h-4" />
                            <span>Casting validé</span>
                          </div>
                          {selectedCollab.casting_validated ? (
                            <div className="text-green-400">✓</div>
                          ) : (
                            <div className="text-red-400">×</div>
                          )}
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 text-gray-400">
                            <ClipboardCheck className="w-4 h-4" />
                            <span>Post publié</span>
                          </div>
                          {selectedCollab.post_progress === '100' ? (
                            <div className="text-green-400">✓</div>
                          ) : (
                            <div className="text-red-400">×</div>
                          )}
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 text-gray-400">
                            <FileInvoice className="w-4 h-4" />
                            <span>Facture envoyée</span>
                          </div>
                          {selectedCollab.invoice_number ? (
                            <div className="text-green-400">✓</div>
                          ) : (
                            <div className="text-red-400">×</div>
                          )}
                        </div>
                        <div className="flex items-center justify-between">
                          
                          <div className="flex items-center gap-2 text-gray-400">
                            <DollarSign className="w-4 h-4" />
                            <span>Paiement reçu</span>
                          </div>
                          {selectedCollab.payment_received ? (
                            <div className="text-green-400">✓</div>
                          ) : (
                            <div className="text-red-400">×</div>
                          )}
                        </div>
                      </div>
                    
                    </div>

                    <div>
                      <h3 className="text-lg font-medium text-white mb-4">Influenceurs</h3>
                      <div className="space-y-4">
                        {selectedCollab.influencers.map((influencer,
                        index) => (
                          <div key={index} className="bg-dark-100 rounded-lg p-4">
                            <div className="flex items-center justify-between mb-4">
                              <div className="flex items-center gap-2">
                                <User className="w-4 h-4 text-accent-purple" />
                                <span className="text-white">{influencer.fullName}</span>
                              </div>
                              <span className="text-gray-400">{formatCurrency(influencer.rate)}</span>
                            </div>
                            <div className="space-y-2">
                              {influencer.deliverables.map((deliverable, dIndex) => (
                                <div key={dIndex} className="flex items-center justify-between text-sm">
                                  <div className="flex items-center gap-3">
                                    <div
                                      className={`px-2 py-1 rounded-lg transition-colors flex items-center gap-1.5 ${
                                        deliverable.posted
                                          ? 'bg-green-500/20 text-green-300 border border-green-500/20'
                                          : 'bg-gray-500/20 text-gray-300 border border-gray-500/20'
                                      }`}
                                    >
                                      <Check className="w-4 h-4" />
                                      <span className="text-xs font-medium">
                                        {deliverable.posted ? 'Posté' : 'Non posté'}
                                      </span>
                                    </div>
                                    <span className="text-gray-400">{CONTENT_TYPES.find(t => t.value === deliverable.type)?.label}</span>
                                  </div>
                                  <span className="text-gray-400">{formatCurrency(deliverable.price)}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </Dialog>

      {/* Add/Edit Modal */}
      <Dialog
        open={isModalOpen}
        onClose={() => {}}
        className="fixed inset-0 z-10 overflow-y-auto"
      >
        <div className="flex items-center justify-center min-h-screen p-4">
          <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-30" />
          <div className="relative bg-dark-50 rounded-xl max-w-4xl w-full mx-4 p-6 border border-dark-200">
            <div className="absolute top-4 right-4">
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
  <div>
    <h2 className="text-2xl font-bold text-white mb-6">
      {selectedCollab ? 'Modifier la collaboration' : 'Nouvelle collaboration'}
    </h2>

    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-200">
            Nom de la campagne
          </label>
          <input
            type="text"
            id="name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="mt-1 block w-full"
            required
          />
        </div>

        <div>
          <label htmlFor="type" className="block text-sm font-medium text-gray-200">
            Type de collaboration
          </label>
          <select
            id="type"
            value={formData.type}
            onChange={(e) => setFormData({ ...formData, type: e.target.value as Collab['type'] })}
            className="mt-1 block w-full"
            required
          >
            {COLLAB_TYPES.map((type) => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label htmlFor="brand" className="block text-sm font-medium text-gray-200">
          Marque
        </label>
        <div className="mt-1 relative">
          <input
            type="text"
            id="brand"
            value={formData.brand}
            className="block w-full pr-10"
            required
            readOnly
            onClick={() => setIsClientSelectorOpen(true)}
            placeholder="Sélectionner un client"
          />
          <button
            type="button"
            onClick={() => setIsClientSelectorOpen(true)}
            className="absolute inset-y-0 right-0 px-3 flex items-center"
          >
            <Search className="w-4 h-4 text-gray-400" />
          </button>
        </div>
      </div>

      {/* Nouveaux champs à ajouter ici */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="quote_number" className="block text-sm font-medium text-gray-200">
            Numéro de devis
          </label>
          <input
            type="text"
            id="quote_number"
            value={formData.quote_number || ''}
            onChange={(e) => setFormData({ ...formData, quote_number: e.target.value })}
            className="mt-1 block w-full"
          />
        </div>

        <div>
          <label htmlFor="invoice_number" className="block text-sm font-medium text-gray-200">
            Numéro de facture
          </label>
          <input
            type="text"
            id="invoice_number"
            value={formData.invoice_number || ''}
            onChange={(e) => setFormData({ ...formData, invoice_number: e.target.value })}
            className="mt-1 block w-full"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label htmlFor="contactDate" className="block text-sm font-medium text-gray-200">
            Date de contact
          </label>
          <input
            type="date"
            id="contactDate"
            value={formData.contactDate?.toISOString().split('T')[0]}
            onChange={(e) => setFormData({ ...formData, contactDate: new Date(e.target.value) })}
            className="mt-1 block w-full"
            required
          />
        </div>

        <div>
          <label htmlFor="signatureDate" className="block text-sm font-medium text-gray-200">
            Date de signature
          </label>
          <input
            type="date"
            id="signatureDate"
            value={formData.signatureDate?.toISOString().split('T')[0]}
            onChange={(e) => setFormData({ ...formData, signatureDate: new Date(e.target.value) })}
            className="mt-1 block w-full"
            required
          />
        </div>

        <div>
          <label htmlFor="manager" className="block text-sm font-medium text-gray-200">
            Chef de projet
          </label>
          <input
            type="text"
            id="manager"
            value={formData.manager}
            onChange={(e) => setFormData({ ...formData, manager: e.target.value })}
            className="mt-1 block w-full"
            required
          />
        </div>
      </div>


                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-medium text-white">Influenceurs</h3>
                      <button
                        type="button"
                        onClick={() => setIsInfluencerSelectorOpen(true)}
                        className="flex items-center gap-2 px-3 py-2 text-sm text-accent-blue hover:text-accent-blue/80"
                      >
                        <Plus className="w-4 h-4" />
                        Ajouter un influenceur
                      </button>
                    </div>

                    <div className="space-y-4">
                      {formData.influencers?.map((influencer, index) => (
                        <div key={index} className="bg-dark-100 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-2">
                              <User className="w-4 h-4 text-accent-purple" />
                              <span className="text-white">{influencer.fullName}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-gray-400">{formatCurrency(influencer.rate)}</span>
                              <button
                                type="button"
                                onClick={() => handleRemoveInfluencer(index)}
                                className="text-red-500 hover:text-red-400"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>

                          <div className="space-y-4">
                            {influencer.deliverables?.map((deliverable, dIndex) => (
                              <div key={dIndex} className="grid grid-cols-3 gap-4 items-start">
                                <div>
                                  <select
                                    value={deliverable.type}
                                    onChange={(e) => handleUpdateDeliverable(index, dIndex, 'type', e.target.value)}
                                    className="block w-full"
                                  >
                                    {CONTENT_TYPES.map((type) => (
                                      <option key={type.value} value={type.value}>{type.label}</option>
                                    ))}
                                  </select>
                                </div>
                                <div>
                                  <input
                                    type="text"
                                    value={deliverable.description}
                                    onChange={(e) => handleUpdateDeliverable(index, dIndex, 'description', e.target.value)}
                                    placeholder="Description"
                                    className="block w-full"
                                  />
                                </div>
                                <div className="flex items-center gap-2 relative">
                                  <input
                                    type="number"
                                    value={deliverable.price}
                                    onChange={(e) => handleUpdateDeliverable(index, dIndex, 'price', e.target.value)}
                                    placeholder="Prix"
                                    className="block w-full"
                                  />
                                  <button
                                   type="button"
                                   onClick={() => {
                                     const updatedDeliverables = [...(formData.influencers![index].deliverables || [])];
                                     updatedDeliverables[dIndex] = {
                                       ...updatedDeliverables[dIndex],
                                       posted: !updatedDeliverables[dIndex].posted
                                     };
                                     const updatedInfluencers = [...(formData.influencers || [])];
                                     updatedInfluencers[index] = {
                                       ...updatedInfluencers[index],
                                       deliverables: updatedDeliverables
                                     };
                                     setFormData({ ...formData, influencers: updatedInfluencers });
                                   }}
                                   className={`px-2 py-1 rounded-lg transition-colors flex items-center gap-1.5 ${
                                     deliverable.posted
                                       ? 'bg-green-500/20 text-green-300 border border-green-500/20'
                                       : 'bg-gray-500/20 text-gray-300 border border-gray-500/20'
                                   }`}
                                 >
                                   <Check className="w-4 h-4" />
                                   <span className="text-xs font-medium">
                                     {deliverable.posted ? 'Posté' : 'Non posté'}
                                   </span>
                                 </button>
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveDeliverable(index, dIndex)}
                                    className="text-red-500 hover:text-red-400 flex-shrink-0"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>
                            ))}

                            <button
                              type="button"
                              onClick={() => handleAddDeliverable(index)}
                              className="flex items-center gap-2 text-sm text-accent-blue hover:text-accent-blue/80"
                            >
                              <Plus className="w-4 h-4" />
                              Ajouter un livrable
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-medium text-white mb-4">État de la campagne</h3>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Send className="w-5 h-5 text-gray-400" />
                          <span className="text-gray-200">Devis envoyé</span>
                        </div>
                        <Switch
                          checked={formData.quote_sent}
                          onChange={(checked) => setFormData({ ...formData, quote_sent: checked })}
                          className={`${
                            formData.quote_sent ? 'bg-accent-blue' : 'bg-dark-200'
                          } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none`}
                        >
                          <span
                            className={`${
                              formData.quote_sent ? 'translate-x-6' : 'translate-x-1'
                            } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                          />
                        </Switch>
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <FileText className="w-5 h-5 text-gray-400" />
                          <span className="text-gray-200">Contrat signé</span>
                        </div>
                        <Switch
                          checked={formData.contract_signed}
                          onChange={(checked) => setFormData({ ...formData, contract_signed: checked })}
                          className={`${
                            formData.contract_signed ? 'bg-accent-blue' : 'bg-dark-200'
                          } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none`}
                        >
                          <span
                            className={`${
                              formData.contract_signed ? 'translate-x-6' : 'translate-x-1'
                            } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                          />
                        </Switch>
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <ClipboardCheck className="w-5 h-5 text-gray-400" />
                          <span className="text-gray-200">Posté</span>
                        </div>
                        <Switch
                          checked={formData.post_progress === '100'}
                          onChange={(checked) => setFormData({ ...formData, post_progress: checked ? '100' : '0' })}
                          className={`${
                            formData.post_progress === '100' ? 'bg-accent-blue' : 'bg-dark-200'
                          } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none`}
                        >
                          <span
                            className={`${
                              formData.post_progress === '100' ? 'translate-x-6' : 'translate-x-1'
                            } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                          />
                        </Switch>
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <UserCheck className="w-5 h-5 text-gray-400" />
                          <span className="text-gray-200">Casting envoyé</span>
                        </div>
                        <Switch
                          checked={formData.casting_validated}
                          onChange={(checked) => setFormData({ ...formData, casting_validated: checked })}
                          className={`${
                            formData.casting_validated ? 'bg-accent-blue' : 'bg-dark-200'
                          } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none`}
                        >
                          <span
                            className={`${
                              formData.casting_validated ? 'translate-x-6' : 'translate-x-1'
                            } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                          />
                        </Switch>
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <FileInvoice className="w-5 h-5 text-gray-400" />
                          <span className="text-gray-200">Facture envoyée</span>
                        </div>
                        <Switch
                          checked={!!formData.invoice_number}
                          onChange={(checked) => setFormData({ ...formData, invoice_number: checked ? 'sent' : '' })}
                          className={`${
                            formData.invoice_number ? 'bg-accent-blue' : 'bg-dark-200'
                          } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none`}
                        >
                          <span
                            className={`${
                              formData.invoice_number ? 'translate-x-6' : 'translate-x-1'
                            } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                          />
                        </Switch>
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <DollarSign className="w-5 h-5 text-gray-400" />
                          <span className="text-gray-200">Paiement reçu</span>
                        </div>
                        <Switch
                          checked={formData.payment_received}
                          onChange={(checked) => setFormData({ ...formData, payment_received: checked })}
                          className={`${
                            formData.payment_received ? 'bg-accent-blue' : 'bg-dark-200'
                          } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none`}
                        >
                          <span
                            className={`${
                              formData.payment_received ? 'translate-x-6' : 'translate-x-1'
                            } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                          />
                        </Switch>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 text-sm font-medium text-gray-300 bg-dark-200 border border-dark-300 rounded-lg hover:bg-dark-300"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-accent-blue border border-transparent rounded-lg hover:bg-accent-blue/90"
                >
                  <Check className="w-4 h-4" />
                  {selectedCollab ? 'Modifier' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </Dialog>

      {/* Client Selector Modal */}
      <Dialog
        open={isClientSelectorOpen}
        onClose={() => {}}
        className="fixed inset-0 z-20 overflow-y-auto"
      >
        <div className="flex items-center justify-center min-h-screen p-4">
          <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-30" />
          <div className="relative bg-dark-50 rounded-xl max-w-2xl w-full mx-4 p-6 border border-dark-200">
            <div className="absolute top-4 right-4">
              <button
                onClick={() => setIsClientSelectorOpen(false)}
                className="text-gray-400 hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-white">Sélectionner un client</h2>

              <div className="relative">
                <input
                  type="text"
                  value={clientSearch}
                  onChange={(e) => setClientSearch(e.target.value)}
                  placeholder="Rechercher un client..."
                  className="block w-full pr-10 pl-10"
                />
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              </div>

              <div className="space-y-2 max-h-96 overflow-y-auto">
                {clients
                  .filter(client =>
                    client.brand_name.toLowerCase().includes(clientSearch.toLowerCase())
                  )
                  .map((client) => (
                    <div
                      key={client.id}
                      onClick={() => handleSelectClient(client)}
                      className="w-full flex items-center p-4 bg-dark-100 rounded-lg hover:bg-dark-200 transition-colors cursor-pointer"
                    >
                      <Building2 className="w-5 h-5 text-accent-blue mr-3" />
                      <div className="text-left">
                        <h3 className="text-white font-medium">{client.brand_name}</h3>
                        {client.contacts[0] && (
                          <p className="text-sm text-gray-400">
                            {client.contacts[0].name} • {client.contacts[0].email}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>
      </Dialog>

      {/* Influencer Selector Modal */}
      <Dialog
        open={isInfluencerSelectorOpen}
        onClose={() => {}}
        className="fixed inset-0 z-20 overflow-y-auto"
      >
        <div className="flex items-center justify-center min-h-screen p-4">
          <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-30" />
          <div className="relative bg-dark-50 rounded-xl max-w-2xl w-full mx-4 p-6 border border-dark-200">
            <div className="absolute top-4 right-4">
              <button
                onClick={() => setIsInfluencerSelectorOpen(false)}
                className="text-gray-400 hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-white">Sélectionner un influenceur</h2>

              <div className="relative">
                <input
                  type="text"
                  value={influencerSearch}
                  onChange={(e) => setInfluencerSearch(e.target.value)}
                  placeholder="Rechercher un influenceur..."
                  className="block w-full pr-10 pl-10"
                />
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              </div>

              <div className="space-y-2 max-h-96 overflow-y-auto">
                {influencers
                  .filter(inf =>
                    inf.fullName.toLowerCase().includes(influencerSearch.toLowerCase()) &&
                    !formData.influencers?.some(i => i.fullName === inf.fullName)
                  )
                  .map((influencer) => (
                    <div
                      key={influencer.id}
                      onClick={() => handleSelectInfluencer(influencer)}
                      className="w-full flex items-center p-4 bg-dark-100 rounded-lg hover:bg-dark-200 transition-colors cursor-pointer"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <User className="w-5 h-5 text-accent-purple" />
                          <h3 className="text-white font-medium">{influencer.fullName}</h3>
                        </div>
                        <div className="mt-1 flex items-center gap-3">
                          {influencer.platforms.map((platform, index) => (
                            <div key={index} className="flex items-center gap-1">
                              <img
                                src={PLATFORM_ICONS[platform.name]?.icon}
                                alt={platform.name}
                                className={`w-4 h-4 ${PLATFORM_ICONS[platform.name]?.color}`}
                              />
                              <span className="text-sm text-gray-400">{platform.audience}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog
        open={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        className="fixed inset-0 z-10 overflow-y-auto"
      >
        <div className="flex items-center justify-center min-h-screen p-4">
          <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-30" />
          <div className="relative bg-dark-50 rounded-xl max-w-md w-full mx-4 p-6 border border-dark-200">
            <div className="mb-6">
              <h3 className="text-lg font-medium text-white">Confirmer la suppression</h3>
              <p className="mt-2 text-sm text-gray-400">
                Êtes-vous sûr de vouloir supprimer la collaboration "{selectedCollab?.name}" ? Cette action est irréversible.
              </p>
            </div>

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setIsDeleteModalOpen(false)}
                className="px-4 py-2 text-sm font-medium text-gray-300 bg-dark-200 border border-dark-300 rounded-lg hover:bg-dark-300"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleDelete}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-lg hover:bg-red-700"
              >
                <Trash2 className="w-4 h-4" />
                Supprimer
              </button>
            </div>
          </div>
        </div>
      </Dialog>
    </div>
  );
}

export default Collaborations;